import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ChartService } from '@app/finxone-web-frontend/app/lib/services/chart-service/chart-service.service';
import {
  TransactionSummaryResponse,
  WeeklyTransactionSummaryResponse,
} from '@app/finxone-web-frontend/app/lib/services/chart-service/chart.type';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import {
  ChartDataContext,
  ChartDateRange,
  ChartLegendPosition,
  ChartSegmentValueType,
  ChartTypeEnum,
  getCssVariableValue,
  getMonthDaysLabels,
  getMonthLabels,
  getMonthWeeksLabels,
  getWeekLabels,
  getWeekRangeLabel,
  getWeekRangeValue,
  isCircularChartType,
} from '@app/finxone-web-frontend/app/lib/utils/chart.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseCssConfig,
  baseCssConfigStyle,
  BaseCssProperties,
  CssConfig,
  isMultiCurrencyAccountInfoType,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { ChartConfiguration, ChartType } from 'chart.js';
import dayjs from 'dayjs';
import Handlebars from 'handlebars';
import { BaseChartDirective } from 'ng2-charts';
import { debounceTime, distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-chart-widget',
  templateUrl: './chart-widget.component.html',
})
export class ChartWidgetComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @ViewChild('chartCanvas', { static: true }) canvas!: ElementRef;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  public textInputHeaderTemplateFunction: HandlebarsTemplateDelegate<Record<string, unknown>>;
  public legendInputFunctions: Map<ChartLegendPosition, HandlebarsTemplateDelegate<Record<string, unknown>>> =
    new Map();
  @Select(AccountState.getCurrentAccountId)
  currentAccountId$!: Observable<string>;
  public ChartTypeEnum = ChartTypeEnum;
  public ChartLegendPosition = ChartLegendPosition;
  public chartType = ChartTypeEnum.BAR;
  public selectedDateRange: string;
  public xAxisRange = ChartDateRange.WEEK;

  public chartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1,
        borderRadius: {
          topLeft: 2,
          topRight: 2,
          bottomLeft: 0,
          bottomRight: 0,
        },
      },
    ],
  };
  public circularChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [], borderWidth: 0 }],
  };
  public chartOptions: ChartConfiguration['options'];
  public circularChartOptions: ChartConfiguration['options'];
  public primaryColor: string;
  public fontFamily: string;
  public chartLayoutSwitch: any;
  public chartLayout: ChartTypeEnum[];
  public chartContentStyle = {};
  public chartWidgetGeneralStyle: baseCssConfigStyle;
  public activeLayoutSwitchIndex = 0;
  public baseCssConfig: BaseCssConfig;
  public chartLayoutSwitchIconStyle: { width: string; height: string };
  public legendPositions: ChartLegendPosition[] = [ChartLegendPosition.BOTTOM];
  public legendInputMap: Record<string, string> = {};
  public remainder = 0;
  public segmentSum = 0;
  public segmentTotalPercentage: number[] = [];
  private baseHandleBarModel: any;
  public dateRangeOptions: { label: string; value: string }[];
  public startDate: string;
  public endDate: string;
  public accountId = '';

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private chartService: ChartService,
    private configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.baseCssConfig = this.dataIn.attri?.css_config;
    this.chartLayout = this.dataIn.attri?.widgetProperties?.['chartLayout'];
    this.chartContentStyle = this.getChartCssConfig(this.dataIn.attri?.widgetProperties['chartCssConfig']);
    this.legendPositions =
      this.dataIn.attri?.widgetProperties?.['legendPositions'] ??
      (this.dataIn.attri?.widgetProperties?.['legendPosition']
        ? [this.dataIn.attri.widgetProperties['legendPosition']]
        : [ChartLegendPosition.BOTTOM]);
    this.legendInputMap = this.dataIn.attri?.widgetProperties?.['legendInputMap'] ?? {};
    this.chartWidgetGeneralStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.BORDER_V2,
      BaseCssProperties.COLORS_V2,
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BOX_SHADOW,
    ]);
    this.xAxisRange = this.dataIn.attri?.widgetProperties?.['chartDateRange'] ?? ChartDateRange.WEEK;
    this.chartType = this.chartLayout?.[0];
    this.getTextInputHeader();
    this.getDateRangeOption();
    this.getChartLayoutSwitchStyles();

    requestAnimationFrame(() => {
      if (this.isFormIdMode) {
        this.buildCircularChartOptions();
        this.refreshCircularChart();
      } else {
        this.getChartOptions();
      }
    });

    if (!this.isFormIdMode) {
      this.updateChartData(this.xAxisRange, this.chartType);
    }

    // Watch form state changes to keep circular chart live
    if (this.isFormIdMode) {
      this.store
        .select(FormActionState.getFormActionStateWithId(this.selectedFormId))
        .pipe(
          debounceTime(200),
          distinctUntilChanged((prev, curr) => {
            // Compare form data values for the relevant input IDs to determine if we should refresh the chart. This prevents unnecessary chart updates when unrelated form data changes.
            const inputIds = (this.dataMappingConfig.segments ?? []).map((s) => s.inputId);
            return inputIds?.every((id) => prev?.formData?.[id] === curr?.formData?.[id]);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.refreshCircularChart());
    }
  }

  public get isFormIdMode(): boolean {
    return this.dataIn.attri?.widgetProperties?.['dataContext'] === ChartDataContext.FORM_ID;
  }

  public get isCircularType(): boolean {
    return isCircularChartType(this.chartType);
  }

  public get selectedFormId(): string {
    return this.dataIn.attri?.widgetProperties?.['selectedFormId'] ?? '';
  }

  public get nativeChartType(): ChartType {
    if (this.chartType === ChartTypeEnum.PROGRESS_ARC) return 'doughnut';
    return this.chartType as ChartType;
  }

  private get dataMappingConfig(): {
    segments?: { inputId: string; color: string; label: string }[];
    segmentValueType?: ChartSegmentValueType;
    denominator?: string;
    denominatorFillColor?: string;
    denominatorLabel?: string;
  } {
    return this.dataIn.attri?.widgetProperties?.['dataMappingConfig'] ?? {};
  }

  private getFormDataValue(inputId: string): number {
    const formState = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(this.selectedFormId),
    );
    const raw = formState?.formData?.[inputId];
    return parseFloat(raw) || 0;
  }

  private getDenominatorValue(): number {
    const denominator = this.dataMappingConfig?.denominator ?? 'accountBalance';
    if (denominator === 'accountBalance') {
      const account = this.store.selectSnapshot(SavingsAccountState.getCurrentAccount);
      if (!account || isMultiCurrencyAccountInfoType(account)) return 0;
      return parseFloat(account.balance ?? '0') || 0;
    }
    return parseFloat(denominator) || 0;
  }

  public refreshCircularChart(): void {
    const config = this.dataMappingConfig;
    if (!config) return;

    const segments: { inputId: string; color: string; label: string }[] = config.segments ?? [];
    const segmentValueType: ChartSegmentValueType =
      config.segmentValueType ?? ChartSegmentValueType.PERCENTAGE;
    const denominator = this.getDenominatorValue();
    const denominatorFillColor: string = config.denominatorFillColor ?? 'var(--color-border-color)';
    const denominatorFill = getCssVariableValue(denominatorFillColor);

    const dataValues: number[] = [];
    const colors: string[] = [];

    for (const seg of segments) {
      const rawValue = this.getFormDataValue(seg.inputId);
      // For workflow purposes, we will generate random values for the segments instead of using form data values. This allows us to visualize the chart in the workflow without needing to fill out the form data.
      if (this.configService.getActiveConfigType().isWorkflow) {
        dataValues.push(Math.floor(Math.random() * 100));
      } else {
        dataValues.push(rawValue);
      }

      colors.push(getCssVariableValue(seg.color) || seg.color);
    }
    this.segmentTotalPercentage = [...dataValues];

    const segmentLabels = segments.map((s) => s.label || s.inputId);
    const denominatorLabel = config.denominatorLabel;

    if (segmentValueType === ChartSegmentValueType.PERCENTAGE && denominator > 0) {
      // Convert percentage values to absolute amounts relative to the denominator
      const absoluteValues = dataValues.map((v) => (v / 100) * denominator);
      const segmentSum = absoluteValues.reduce((a, b) => a + b, 0);
      this.segmentSum = segmentSum;
      const remainder = Math.max(0, denominator - segmentSum);
      this.remainder = remainder;
      this.circularChartData = {
        labels: [...segmentLabels, denominatorLabel],
        datasets: [
          {
            data: [...absoluteValues, remainder],
            backgroundColor: [...colors, denominatorFill],
            borderWidth: 0,
          },
        ],
      };
    } else {
      // Absolute: each segment is its own value; add denominator fill for remainder
      this.setAbsoluteCircularChartData(
        denominator,
        dataValues,
        segmentLabels,
        denominatorLabel,
        colors,
        denominatorFill,
      );
    }

    this.chart?.update();
  }

  private setAbsoluteCircularChartData(
    denominator: number,
    dataValues: number[],
    segmentLabels: string[],
    denominatorLabel: string | undefined,
    colors: string[],
    denominatorFill: string,
  ): void {
    if (denominator > 0) {
      const segmentSum = dataValues.reduce((a, b) => a + b, 0);
      this.segmentSum = segmentSum;
      const remainder = Math.max(0, denominator - segmentSum);
      this.remainder = remainder;
      this.circularChartData = {
        labels: [...segmentLabels, denominatorLabel],
        datasets: [
          {
            data: [...dataValues, remainder],
            backgroundColor: [...colors, denominatorFill],
            borderWidth: 0,
          },
        ],
      };
    } else {
      this.circularChartData = {
        labels: segmentLabels,
        datasets: [{ data: dataValues, backgroundColor: colors, borderWidth: 0 }],
      };
    }
  }

  private buildCircularChartOptions(): void {
    const isProgressArc = this.chartType === ChartTypeEnum.PROGRESS_ARC;
    this.circularChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout:
        this.chartType === ChartTypeEnum.DOUGHNUT || this.chartType === ChartTypeEnum.PROGRESS_ARC
          ? '80%'
          : 0,
      rotation: isProgressArc ? -90 : 0,
      circumference: isProgressArc ? 180 : 360,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true, boxPadding: 10 },
      },
    } as ChartConfiguration['options'];
  }

  private getDateRangeOption() {
    if (this.xAxisRange === ChartDateRange.WEEK) {
      this.dateRangeOptions = [
        { label: getWeekRangeLabel(0), value: getWeekRangeValue(0) },
        { label: getWeekRangeLabel(1), value: getWeekRangeValue(1) },
        { label: getWeekRangeLabel(2), value: getWeekRangeValue(2) },
        { label: getWeekRangeLabel(3), value: getWeekRangeValue(3) },
      ];
    }
    if (this.xAxisRange === ChartDateRange.MONTH_WEEKS || this.xAxisRange === ChartDateRange.MONTH_DAYS) {
      this.dateRangeOptions = this.getMonthNameRanges();
      this.selectedDateRange = this.dateRangeOptions[0]?.label;
      this.dateRangeChange(this.dateRangeOptions[0]?.value);
    }
    if (this.xAxisRange === ChartDateRange.YEAR) {
      this.dateRangeOptions = this.generateYearRangeOptions();
    }

    if (!this.selectedDateRange) {
      this.selectedDateRange = this.dateRangeOptions[0]?.label;
      this.dateRangeChange(this.dateRangeOptions[0]?.value);
    }
  }

  public dateRangeChange(event: string) {
    const [startDate, endDate] = event.split(',');
    this.startDate = startDate;
    this.endDate = endDate;
    this.updateChartData(this.xAxisRange, this.chartType);
    if (!this.isFormIdMode) {
      this.getChartData();
    }
  }

  private async getChartData() {
    if (this.isFormIdMode) return;

    try {
      const id = await firstValueFrom(
        this.store
          .select(AccountState.getCurrentAccountId)
          .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$)),
        { defaultValue: null },
      );

      if (!id) return;

      const data = await firstValueFrom(
        this.chartService.getChartDataByContext(
          this.dataIn.attri?.widgetProperties?.['dataContext'],
          id,
          this.startDate,
          this.endDate,
          this.xAxisRange,
        ),
        { defaultValue: [] as TransactionSummaryResponse[] },
      );

      const output = this.generateDateRangeDataset(data, this.startDate, this.endDate);
      this.chartData.datasets[0].data = output;
      this.chart?.update();
    } catch (err) {
      console.error('Error fetching chart data', err);
    }
  }

  private generateDateRangeDataset(
    data: TransactionSummaryResponse[] | WeeklyTransactionSummaryResponse[],
    startDate: string,
    endDate: string,
  ): number[] {
    let result: number[] = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (this.xAxisRange) {
      case ChartDateRange.YEAR:
        for (let i = 0; i < 12; i++) {
          const monthStartDate = start.clone().add(i, 'months').startOf('month');
          const monthKey = monthStartDate.format('YYYY-MM');
          const matchingData = (data as TransactionSummaryResponse[]).find((item) => item.date === monthKey);
          result.push(matchingData ? matchingData.totalAmount : 0);
        }
        break;

      case ChartDateRange.MONTH_WEEKS:
        result = (data as WeeklyTransactionSummaryResponse[]).map((item) => item.totalAmount);
        break;

      default: {
        const totalDays = end.diff(start, 'days') + 1;
        for (let i = 0; i < totalDays; i++) {
          const current = start.clone().add(i, 'days');
          const dayKey = current.format('YYYY-MM-DD');
          const matchingData = (data as TransactionSummaryResponse[]).find((item) => item.date === dayKey);
          result.push(matchingData ? matchingData.totalAmount : 0);
        }
        break;
      }
    }

    return result;
  }

  public getChartLayoutSwitchStyles() {
    this.chartLayoutSwitch = this.dataIn.attri?.widgetProperties?.['chartLayoutSwitchCssConfig'];
    this.chartLayoutSwitchIconStyle = {
      width: `${this.chartLayoutSwitch?.iconSize}${this.chartLayoutSwitch?.iconUnit}`,
      height: `${this.chartLayoutSwitch?.iconSize}${this.chartLayoutSwitch?.iconUnit}`,
    };
  }

  private getTextInputHeader() {
    this.textInputHeaderTemplateFunction = Handlebars.compile(
      this.dataIn.attri?.widgetProperties?.['textInputHeader'] ?? '',
    );

    const inputMap: Record<string, string> = this.dataIn.attri?.widgetProperties?.['legendInputMap'] ?? {};
    const legacyInput: string = this.dataIn.attri?.widgetProperties?.['legendInput'] ?? '';

    for (const pos of this.legendPositions) {
      const raw = inputMap[pos] ?? (this.legendPositions.indexOf(pos) === 0 ? legacyInput : '');
      this.legendInputFunctions.set(pos as ChartLegendPosition, Handlebars.compile(raw));
    }

    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  public renderTextInputHeader() {
    return this.textInputHeaderTemplateFunction({ ...this.baseHandleBarModel });
  }

  public legendTemplate(position: ChartLegendPosition): string {
    const fn = this.legendInputFunctions.get(position);
    if (!fn) return '';
    return fn({
      ...this.baseHandleBarModel,
      remainder: this.remainder, // Provide remainder for legends that want to show the leftover value until the denominator
      segmentSum: this.segmentSum, // Provide segment sum and remainder for legends that want to show these values
      segmentTotalPercentage: this.segmentTotalPercentage.reduce((a, b) => a + b, 0), // Provide total value for legends that want to show percentage or absolute values based on total
    });
  }

  private getChartOptions() {
    const chartDataColor = getCssVariableValue(this.dataIn.attri?.widgetProperties?.['chartDataColor']);
    const chartGridColor = getCssVariableValue(this.dataIn.attri?.widgetProperties?.['chartGridColor']);
    const chartLabelTextColor = getCssVariableValue(
      this.dataIn.attri?.widgetProperties?.['chartLabelTextColor'],
    );
    const fontFamily = getCssVariableValue('var(--font-family)');

    this.chartData.datasets[0].backgroundColor = chartDataColor;
    this.chartData.datasets[0].borderColor = chartDataColor;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true, boxPadding: 10, titleSpacing: 10 },
      },
      scales: {
        x: {
          grid: { display: true, drawTicks: false, color: chartGridColor },
          ticks: {
            align: 'center',
            font: { size: 14, weight: 'normal', family: fontFamily },
            maxRotation: 30,
            minRotation: 30,
            color: chartLabelTextColor,
          },
        },
        y: {
          beginAtZero: true,
          grid: { display: true, drawTicks: false, color: chartGridColor },
          ticks: {
            font: { size: 14, weight: 'normal', family: fontFamily },
            color: chartLabelTextColor,
          },
        },
      },
    };
  }

  public updateChartData(type: ChartDateRange, chartType: ChartTypeEnum): void {
    this.chartType = chartType;

    if (this.isFormIdMode) {
      this.buildCircularChartOptions();
      this.refreshCircularChart();
      return;
    }

    switch (type) {
      case ChartDateRange.MONTH_DAYS:
        this.chartData.labels = getMonthDaysLabels(this.startDate, this.endDate);
        break;
      case ChartDateRange.MONTH_WEEKS:
        this.chartData.labels = getMonthWeeksLabels(this.endDate);
        break;
      case ChartDateRange.WEEK:
        this.chartData.labels = getWeekLabels();
        break;
      case ChartDateRange.YEAR:
        this.chartData.labels = getMonthLabels();
        break;
      default:
        this.chartData.labels = [];
        break;
    }

    this.getTabStyle(this.chartLayout?.[0], 1);
  }

  public getContainerStyle(): { [key: string]: string } {
    const borderRadiusUnit = this.chartLayoutSwitch?.borderRadiusUnit;
    if (this.chartLayoutSwitch?.borderClass === 'none') {
      return {
        'border-top-left-radius': `${this.chartLayoutSwitch?.borderRadiusTopLeft}${borderRadiusUnit}`,
        'border-top-right-radius': `${this.chartLayoutSwitch?.borderRadiusTopRight}${borderRadiusUnit}`,
        'border-bottom-right-radius': `${this.chartLayoutSwitch?.borderRadiusBottomRight}${borderRadiusUnit}`,
        'border-bottom-left-radius': `${this.chartLayoutSwitch?.borderRadiusBottomLeft}${borderRadiusUnit}`,
      };
    }
    return {};
  }

  public getTabStyle(tabType: ChartTypeEnum, index: number): { [key: string]: string } {
    const isActive = this.chartType === tabType;
    const isFirst = index === 0;
    const isLast = index === (this.chartLayout?.length ?? 1) - 1;
    const borderWidthUnit = this.chartLayoutSwitch?.borderWidthUnit;
    const borderWidth = (v: number | undefined) => `${v}${borderWidthUnit}`;

    const style: { [key: string]: string } = {
      'background-color': isActive
        ? this.chartLayoutSwitch?.activeBackgroundColor
        : this.chartLayoutSwitch.idleBackgroundColor,
      color: isActive ? this.chartLayoutSwitch?.activeTextColor : this.chartLayoutSwitch?.idleTextColor,
      'border-color': isActive
        ? this.chartLayoutSwitch?.activeBorderColor
        : this.chartLayoutSwitch?.idleBorderColor,
      'border-top-width': borderWidth(this.chartLayoutSwitch?.borderWidthTop),
      // Collapse left border on every tab after the first to avoid double borders
      'border-left-width': isFirst ? borderWidth(this.chartLayoutSwitch?.borderWidthLeft) : '0px',
      'border-right-width': borderWidth(this.chartLayoutSwitch?.borderWidthRight),
      'border-bottom-width': borderWidth(this.chartLayoutSwitch?.borderWidthBottom),
      'border-style': this.chartLayoutSwitch?.borderStyle ?? 'solid',
      cursor: 'pointer',
    };

    if (isFirst) {
      style['border-top-left-radius'] = 'inherit';
      style['border-bottom-left-radius'] = 'inherit';
    }
    if (isLast) {
      style['border-top-right-radius'] = 'inherit';
      style['border-bottom-right-radius'] = 'inherit';
    }

    return style;
  }

  public getLayoutSwitchIcon(index: number): string {
    if (index === 0) return this.chartLayoutSwitch?.leftIcon || 'bar-chart';
    if (index === 1 && this.chartLayoutSwitch?.middleIcon) return this.chartLayoutSwitch.middleIcon;
    return this.chartLayoutSwitch?.rightIcon || 'line-chart';
  }

  public getChartCssConfig(cssConfig: CssConfig) {
    return {
      'margin-top': `${cssConfig?.marginTop}${cssConfig?.marginUnit}`,
      'margin-left': `${cssConfig?.marginLeft}${cssConfig?.marginUnit}`,
      'margin-right': `${cssConfig?.marginRight}${cssConfig?.marginUnit}`,
      'margin-bottom': `${cssConfig?.marginBottom}${cssConfig?.marginUnit}`,
      'padding-top': `${cssConfig?.paddingTop}${cssConfig?.paddingUnit}`,
      'padding-left': `${cssConfig?.paddingLeft}${cssConfig?.paddingUnit}`,
      'padding-right': `${cssConfig?.paddingRight}${cssConfig?.paddingUnit}`,
      'padding-bottom': `${cssConfig?.paddingBottom}${cssConfig?.paddingUnit}`,
    };
  }

  public get headerAlignment(): string {
    if (this.dataIn.attri?.widgetProperties?.['chartRhsAlignment'] != null)
      return this.dataIn.attri?.widgetProperties?.['chartRhsAlignment'];
    return 'space-between';
  }

  /**
   * Builds month-based date range options for a chart filter.
   *
   * Rules:
   * - Includes up to 12 months total (current month + previous 11).
   * - Never includes months before the account creation month (if available).
   * - Labels:
   *   - "This month" for current month
   *   - "Last month" for previous month
   *   - "MMM YYYY" for older months
   * - Value format: "YYYY-MM-DD,YYYY-MM-DD"
   *
   * @returns Month range options ordered newest to oldest.
   */

  private getMonthNameRanges() {
    const now = dayjs();
    const currentMonthStart = now.startOf('month');
    const accountCreatedDate = this.getAccountCreatedDate();

    const accountCreatedMonthStart = accountCreatedDate
      ? accountCreatedDate.startOf('month')
      : currentMonthStart;

    // Last 12 months window including current month.
    const twelveMonthsWindowStart = currentMonthStart.subtract(11, 'months');

    // Do not go earlier than account creation month.
    const earliestMonth = accountCreatedMonthStart.isAfter(twelveMonthsWindowStart)
      ? accountCreatedMonthStart
      : twelveMonthsWindowStart;

    const ranges: { label: string; value: string }[] = [];

    for (
      let monthCursor = currentMonthStart;
      monthCursor.isAfter(earliestMonth) || monthCursor.isSame(earliestMonth, 'month');
      monthCursor = monthCursor.subtract(1, 'month')
    ) {
      const startDate = monthCursor.startOf('month').format('YYYY-MM-DD');
      const endDate = monthCursor.endOf('month').format('YYYY-MM-DD');

      let label: string;
      if (monthCursor.isSame(currentMonthStart, 'month')) {
        label = 'This month';
      } else if (monthCursor.isSame(currentMonthStart.subtract(1, 'month'), 'month')) {
        label = 'Last month';
      } else {
        label = monthCursor.format('MMM YYYY');
      }

      ranges.push({ label, value: `${startDate},${endDate}` });
    }

    return ranges;
  }

  private getAccountCreatedDate(): dayjs.Dayjs | null {
    const currentAccount = this.store.selectSnapshot(AccountState.getCurrentAccount);
    return currentAccount?.accountCreatedDate ? dayjs(currentAccount.accountCreatedDate) : null;
  }

  private generateYearRangeOptions() {
    const currentYear = dayjs().year();
    const accountCreatedDate = this.getAccountCreatedDate();
    const accountCreatedYear = accountCreatedDate ? accountCreatedDate.year() : currentYear;

    const years: { label: string; value: string }[] = [];
    for (let year = currentYear; year >= accountCreatedYear; year--) {
      const startDate = dayjs().year(year).startOf('year').format('YYYY-MM-DD');
      const endDate = dayjs().year(year).endOf('year').format('YYYY-MM-DD');
      years.push({ label: year.toString(), value: `${startDate},${endDate}` });
    }
    return years;
  }

  ngOnDestroy() {
    this.chartData = { labels: [], datasets: [] };
    this.circularChartData = { labels: [], datasets: [] };
    this.chartOptions = {};
    this.circularChartOptions = {};
  }
}
